import { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import backend from '../services/backend';
import { Card } from './Card';
import { formatDateOnly } from '../lib/dateFormat';

function formatWeekLabel(isoDate: string) {
  const d = new Date(`${isoDate}T12:00:00.000Z`);
  return formatDateOnly(d);
}

function pct1(n: number | null | undefined) {
  if (n == null || Number.isNaN(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

export function ClassificationWeeklyTrends() {
  const [showChartHelp, setShowChartHelp] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['classification-weekly'],
    queryFn: async () => (await backend.getClassificationWeekly({ weeks: 16 })).data,
  });

  const chartRows = useMemo(() => {
    if (!data?.buckets?.length) return [];
    return data.buckets.map((b) => ({
      weekStart: b.weekStart,
      weekLabel: formatWeekLabel(b.weekStart),
      mistakePct: b.mistakeRate != null ? b.mistakeRate * 100 : null,
      confidencePct: b.avgTopConfidence != null ? b.avgTopConfidence * 100 : null,
      highConfidencePct: b.highConfidenceRate != null ? b.highConfidenceRate * 100 : null,
    }));
  }, [data?.buckets]);

  const weekOnWeek = useMemo(() => {
    const buckets = data?.buckets;
    if (!buckets || buckets.length < 2) return null;
    const cur = buckets[buckets.length - 1];
    const prev = buckets[buckets.length - 2];
    const mistakeDeltaPp =
      cur.mistakeRate != null && prev.mistakeRate != null ? (cur.mistakeRate - prev.mistakeRate) * 100 : null;
    const confidenceDeltaPp =
      cur.avgTopConfidence != null && prev.avgTopConfidence != null
        ? (cur.avgTopConfidence - prev.avgTopConfidence) * 100
        : null;
    return { mistakeDeltaPp, confidenceDeltaPp };
  }, [data?.buckets]);

  if (isLoading) {
    return (
      <Card>
        <div className="text-sm text-gray-600 dark:text-zinc-400">Loading weekly classification metrics…</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-sm text-red-600 dark:text-red-400">Could not load weekly classification metrics.</div>
      </Card>
    );
  }

  if (!data?.buckets?.length) {
    return (
      <Card>
        <div className="text-sm text-gray-600 dark:text-zinc-400">No classification history for the selected window.</div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Weekly classification quality</h2>
          <p className="text-sm text-gray-600 dark:text-zinc-400 mt-1">
            Model mistake vs assigned category labeled pages, mean top-1 confidence, and user corrections.{' '}
            {weekOnWeek && (weekOnWeek.mistakeDeltaPp != null || weekOnWeek.confidenceDeltaPp != null) && (
              <span className="block sm:inline sm:ml-1 mt-1 sm:mt-0">
                <span className="font-medium text-gray-800 dark:text-zinc-200">Latest week vs prior:</span>{' '}
                {weekOnWeek.mistakeDeltaPp != null && (
                  <span
                    className={
                      weekOnWeek.mistakeDeltaPp < 0
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : weekOnWeek.mistakeDeltaPp > 0
                          ? 'text-red-600 dark:text-red-400'
                          : ''
                    }
                  >
                    mistake rate {weekOnWeek.mistakeDeltaPp > 0 ? '+' : ''}
                    {weekOnWeek.mistakeDeltaPp.toFixed(1)} pp
                  </span>
                )}
                {weekOnWeek.mistakeDeltaPp != null && weekOnWeek.confidenceDeltaPp != null && ', '}
                {weekOnWeek.confidenceDeltaPp != null && (
                  <span
                    className={
                      weekOnWeek.confidenceDeltaPp > 0
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : weekOnWeek.confidenceDeltaPp < 0
                          ? 'text-red-600 dark:text-red-400'
                          : ''
                    }
                  >
                    avg confidence {weekOnWeek.confidenceDeltaPp > 0 ? '+' : ''}
                    {weekOnWeek.confidenceDeltaPp.toFixed(1)} pp
                  </span>
                )}
                .
              </span>
            )}
          </p>
          <p className="text-xs text-gray-500 dark:text-zinc-500 mt-2">{data.timeNote}</p>
          <button
            type="button"
            id="classification-chart-help-toggle"
            aria-expanded={showChartHelp}
            aria-controls="classification-chart-help"
            onClick={() => setShowChartHelp((open) => !open)}
            className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 underline-offset-2 hover:underline"
          >
            {showChartHelp ? 'Hide chart explanation' : 'How to read this chart'}
          </button>
        </div>

        {showChartHelp && (
        <div
          id="classification-chart-help"
          role="region"
          aria-label="How to interpret the classification quality chart"
          className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50/80 dark:bg-zinc-900/40 px-4 py-3 text-sm text-gray-700 dark:text-zinc-300 space-y-3"
        >
          <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">How to read this chart</h3>
          <p className="text-gray-600 dark:text-zinc-400">
            Each point is one <strong className="font-medium text-gray-800 dark:text-zinc-200">week</strong> (UTC, shown on
            the horizontal axis). The vertical axis is always <strong className="font-medium text-gray-800 dark:text-zinc-200">0–100%</strong> so you can
            compare the three measures on the same scale.
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-600 dark:text-zinc-400">
            <li>
              <span className="font-medium text-red-600 dark:text-red-400">Red — Mistake rate (labeled):</span> Among pages
              that already have a <strong className="font-normal text-gray-800 dark:text-zinc-200">human-assigned category</strong>, this is the percentage where
              the model’s top predicted category still does not match that assignment.{' '}
              <span className="text-gray-800 dark:text-zinc-200">Lower is better.</span> If there are few labeled pages in a
              week, this line can swing more from random noise—check the “Labeled” column in the table.
            </li>
            <li>
              <span className="font-medium text-indigo-600 dark:text-indigo-400">Indigo — Avg top-1 confidence:</span> For all
              pages classified that week, the average score the model gives to its first-choice category (higher usually means
              the model is more decisive).{' '}
              <span className="text-gray-800 dark:text-zinc-200">Higher is often better,</span> especially alongside a falling
              mistake rate.
            </li>
            <li>
              <span className="font-medium text-emerald-600 dark:text-emerald-400">Green (dashed) — Share ≥85% confidence:</span>{' '}
              The fraction of classified pages whose top prediction scored at least 85%. This highlights how much volume sits
              in a high-certainty band.{' '}
              <span className="text-gray-800 dark:text-zinc-200">Higher generally means more pages look “clear” to the model.</span>
            </li>
          </ul>
          <p className="text-gray-600 dark:text-zinc-400">
            <strong className="font-medium text-gray-800 dark:text-zinc-200">Improvement over time</strong> often looks like the
            red line trending down while indigo and green trend up. The summary above the chart (“Latest week vs prior”) spells
            out week-on-week change in mistake rate and average confidence in percentage points (pp).
          </p>
          <p className="text-xs text-gray-500 dark:text-zinc-500">
            The table’s <strong className="font-medium text-gray-600 dark:text-zinc-400">User rejects</strong> counts audit
            events when someone first sets a category and it disagrees with the model—not every later edit—so use it as a
            supporting signal alongside mistake rate and confidence.
          </p>
        </div>
        )}

        <div className="h-72 w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart accessibilityLayer={false} data={chartRows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" />
              <XAxis dataKey="weekLabel" tick={{ fontSize: 12 }} />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => `${v}%`}
                label={{ value: 'Percent', angle: -90, position: 'insideLeft', fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--tooltip-bg, white)',
                  border: '1px solid rgb(228 228 231)',
                }}
                wrapperClassName="dark:!bg-zinc-800 dark:!border-zinc-600"
                formatter={(value, name) => [`${Number(value).toFixed(1)}%`, name]}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="mistakePct"
                name="Mistake rate (labeled)"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="confidencePct"
                name="Avg top-1 confidence"
                stroke="#6366f1"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="highConfidencePct"
                name="Share ≥85% confidence"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ r: 2 }}
                connectNulls={false}
                strokeDasharray="4 4"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 dark:bg-zinc-900/50 text-left text-gray-600 dark:text-zinc-400">
              <tr>
                <th className="px-3 py-2 font-medium">Week (UTC)</th>
                <th className="px-3 py-2 font-medium text-right">Classified</th>
                <th className="px-3 py-2 font-medium text-right">Labeled</th>
                <th className="px-3 py-2 font-medium text-right">Errors</th>
                <th className="px-3 py-2 font-medium text-right">Mistake rate</th>
                <th className="px-3 py-2 font-medium text-right">Avg conf.</th>
                <th className="px-3 py-2 font-medium text-right">≥85% share</th>
                <th className="px-3 py-2 font-medium text-right">User rejects</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700 text-gray-900 dark:text-zinc-100">
              {data.buckets.map((b) => (
                <tr key={b.weekStart} className="hover:bg-zinc-50/80 dark:hover:bg-zinc-800/50">
                  <td className="px-3 py-2 whitespace-nowrap">{b.weekStart}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{b.pagesClassified}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{b.labeledPages}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{b.predictionErrors}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct1(b.mistakeRate)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct1(b.avgTopConfidence)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct1(b.highConfidenceRate)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{b.humanCorrections}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}
